import React, { useState } from "react";
import { Map as MapIcon, Search, ExternalLink } from "lucide-react";
import useFetch from "@/lib/useFetch";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import MapPreview from "@/components/common/MapPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate } from "@/lib/communities";

export default function Maps() {
    const { data } = useFetch("/maps");
    const [seed, setSeed] = useState("");
    const [size, setSize] = useState("4000");
    if (!data) return <Loader />;

    const openLookup = () => {
        if (!seed.trim()) return;
        window.open(`https://rustmaps.com/map/${size.trim() || "4000"}_${seed.trim()}`, "_blank", "noopener");
    };

    return (
        <div data-testid="admin-maps">
            <PageHeader title="Maps" subtitle="Map configuration per server. Pull any seed as an interactive map, or edit seeds and sizes under Servers." />

            <div data-testid="admin-map-seed-lookup" className="tactical-card rounded-lg p-4 mb-6">
                <div className="label-mono text-muted-foreground mb-2 flex items-center gap-1"><Search size={12} /> Pull Interactive Map by Seed</div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid="admin-map-seed-input" value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="Map seed (e.g. 184726354)" className="bg-background border-border" />
                    <Input data-testid="admin-map-size-input" value={size} onChange={(e) => setSize(e.target.value)} placeholder="Size (e.g. 4000)" className="bg-background border-border sm:w-40" />
                    <Button data-testid="admin-map-lookup-open" onClick={openLookup} className="font-heading uppercase bg-hazard text-black hover:bg-hazard/90">
                        <ExternalLink size={15} className="mr-1" /> Open Map
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Opens an interactive RustMaps.com render of the seed. Procedural (PC) seeds show monuments and biomes.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.map((m) => (
                    <SectionCard key={m.community_key} testId={`admin-map-${m.community_key}`}
                        title={<span className="flex items-center gap-2"><MapIcon size={16} className="text-hazard" /> {m.community_name}</span>}
                        action={m.monument_pvp ? <StatusBadge status="failed" label="MONUMENT PVP" /> : <StatusBadge status="active" label={m.mode} />}>
                        <MapPreview size={m.map_size} seed={m.map_seed} testId={`admin-map-preview-${m.community_key}`} />
                        <div className="space-y-1.5 text-sm mt-3">
                            <Row label="Server" value={m.server_name} />
                            <Row label="Map Size" value={m.map_size} />
                            <Row label="Seed" value={m.map_seed} mono />
                            <Row label="Save Wipe" value={fmtDate(m.save_wipe)} />
                            <Row label="BP Wipe" value={m.blueprint_wipe ? fmtDate(m.blueprint_wipe) : "Never"} />
                        </div>
                    </SectionCard>
                ))}
            </div>
            <Footer />
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-center justify-between">
            <span className="label-mono text-muted-foreground">{label}</span>
            <span className={`text-foreground ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
        </div>
    );
}
